import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { MaskitoElementPredicate, MaskitoOptions } from '@maskito/core';
import { select, Store } from '@ngrx/store';

import { isEmpty, isEqual, isNull, omit } from 'lodash-es';
import { DateTime } from 'luxon';
import { BehaviorSubject, map, Observable, tap, throttleTime } from 'rxjs';
import { distinctUntilChanged, filter } from 'rxjs/operators';

import { FORM_SECTION } from '../../../../../constants/form.constant';
import { amountMask, rateAmountMask, rateMask } from '../../../../../constants/maskito.constants';
import { NetSheetSplitOptionsEnum, NetSheetStateEnum } from '../../../../../enums/net-sheet';
import * as fromNetSheetActions from '../../../../../store/actions/net-sheet.actions';
import * as fromNetSheetSelectors from '../../../../../store/selectors/net-sheet.selectors';
import { FormSection } from '../../../../../typings/form';
import { NetSheetFormFieldSettings, NetSheetNameValuePair } from '../../../../../typings/net-sheet';
import { GenericComponent } from '../../../../components/generic/generic.component';
import { DatePickerService } from '../../../../services/date-picker.service';

interface FormModel {
  transactionInformationBrokerageFee: FormControl<string | null>;
  transactionInformationBuyerCreditAmount: FormControl<string | null>;
  transactionInformationCityTransferTaxSplit: FormControl<NetSheetSplitOptionsEnum | null>;
  transactionInformationClosingDate: FormControl<string | null>;
  transactionInformationCountyTransferTaxSplit: FormControl<NetSheetSplitOptionsEnum | null>;
  transactionInformationEscrowBranch: FormControl<string | null>;
  transactionInformationEscrowCity: FormControl<string | null>;
  transactionInformationEscrowCounty: FormControl<string | null>;
  transactionInformationEscrowFeeSplit: FormControl<NetSheetSplitOptionsEnum | null>;
  transactionInformationEscrowState: FormControl<NetSheetStateEnum | null>;
  transactionInformationFirpta: FormControl<boolean>;
  transactionInformationFtb: FormControl<boolean>;
  transactionInformationHarpta: FormControl<boolean>;
  transactionInformationHazardInsuranceAmount: FormControl<string | null>;
  transactionInformationLoanPrimaryAmount: FormControl<string | null>;
  transactionInformationLoanPrimaryInterestRate: FormControl<string | null>;
  transactionInformationLoanPrimaryOriginationFees: FormControl<string | null>;
  transactionInformationMortgagePayoffCount: FormControl<number | null>;
  transactionInformationNaturalHazardDisclosure: FormControl<string | null>;
  transactionInformationNewLoan: FormControl<boolean>;
  transactionInformationNotaryFee: FormControl<string | null>;
  transactionInformationOwnersExemption: FormControl<boolean>;
  transactionInformationOwnersPolicySplit: FormControl<NetSheetSplitOptionsEnum>;
  transactionInformationPersonalResidence: FormControl<boolean>;
  transactionInformationPropertyAddress: FormControl<string | null>;
  transactionInformationPropertyCity: FormControl<string | null>;
  transactionInformationPropertyCounty: FormControl<string | null>;
  transactionInformationPropertyIncorporatedArea: FormControl<boolean>;
  transactionInformationPropertyState: FormControl<NetSheetStateEnum | null>;
  transactionInformationPropertyZip: FormControl<string | null>;
  transactionInformationReo: FormControl<boolean>;
  transactionInformationSalesPrice: FormControl<string | null>;
  transactionInformationSewerBill: FormControl<string | null>;
  transactionInformationShortSale: FormControl<boolean>;
  transactionInformationSubEscrow: FormControl<boolean>;
  transactionInformationSurveyAmendmentSplit: FormControl<boolean>;
  transactionInformationWarrantyPremiumAmount: FormControl<string | null>;
  transactionInformationWaterBill: FormControl<string | null>;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: FORM_SECTION,
      useExisting: TransactionInformationComponent
    }
  ],
  selector: 'red-net-sheet-transaction-information',
  standalone: false,
  templateUrl: './transaction-information.component.html'
})
export class TransactionInformationComponent extends GenericComponent implements OnInit, FormSection {
  private store = inject(Store);
  private datePickerService = inject(DatePickerService);

  buyerCreditAmountLabel$: Observable<string>;

  isDatePickerOpened = false;

  form = new FormGroup<FormModel>({
    transactionInformationBrokerageFee: new FormControl<string | null>(null),
    transactionInformationBuyerCreditAmount: new FormControl<string | null>(null),
    transactionInformationCityTransferTaxSplit: new FormControl<NetSheetSplitOptionsEnum>(
      NetSheetSplitOptionsEnum.Seller
    ),
    transactionInformationClosingDate: new FormControl<string | null>(null),
    transactionInformationCountyTransferTaxSplit: new FormControl<NetSheetSplitOptionsEnum>(
      NetSheetSplitOptionsEnum.Seller
    ),
    transactionInformationEscrowBranch: new FormControl<string | null>(null),
    transactionInformationEscrowCity: new FormControl<string | null>(null),
    transactionInformationEscrowCounty: new FormControl<string | null>(null),
    transactionInformationEscrowFeeSplit: new FormControl<NetSheetSplitOptionsEnum>(NetSheetSplitOptionsEnum.Buyer),
    transactionInformationEscrowState: new FormControl<NetSheetStateEnum | null>(null),
    transactionInformationFirpta: new FormControl<boolean>(false, {
      nonNullable: true
    }),
    transactionInformationFtb: new FormControl<boolean>(false, {
      nonNullable: true
    }),
    transactionInformationHarpta: new FormControl<boolean>(false, {
      nonNullable: true
    }),
    transactionInformationHazardInsuranceAmount: new FormControl<string | null>(null),
    transactionInformationLoanPrimaryAmount: new FormControl<string | null>(null),
    transactionInformationLoanPrimaryInterestRate: new FormControl<string | null>(null),
    transactionInformationLoanPrimaryOriginationFees: new FormControl<string | null>(null),
    transactionInformationMortgagePayoffCount: new FormControl<number | null>(null),
    transactionInformationNaturalHazardDisclosure: new FormControl<string | null>(null),
    transactionInformationNewLoan: new FormControl<boolean>(false, {
      nonNullable: true
    }),
    transactionInformationNotaryFee: new FormControl<string | null>(null),
    transactionInformationOwnersExemption: new FormControl<boolean>(false, {
      nonNullable: true
    }),
    transactionInformationOwnersPolicySplit: new FormControl<NetSheetSplitOptionsEnum>(NetSheetSplitOptionsEnum.Buyer, {
      nonNullable: true
    }),
    transactionInformationPersonalResidence: new FormControl<boolean>(false, {
      nonNullable: true
    }),
    transactionInformationPropertyAddress: new FormControl<string | null>(null, { validators: [Validators.required] }),
    transactionInformationPropertyCity: new FormControl<string | null>(null),
    transactionInformationPropertyCounty: new FormControl<string | null>(null),
    transactionInformationPropertyIncorporatedArea: new FormControl<boolean>(false, {
      nonNullable: true
    }),
    transactionInformationPropertyState: new FormControl<NetSheetStateEnum | null>(null),
    transactionInformationPropertyZip: new FormControl<string | null>(null, {
      validators: [Validators.minLength(5), Validators.maxLength(5)]
    }),
    transactionInformationReo: new FormControl<boolean>(false, {
      nonNullable: true
    }),
    transactionInformationSalesPrice: new FormControl<string | null>(null),
    transactionInformationSewerBill: new FormControl<string | null>(null),
    transactionInformationShortSale: new FormControl<boolean>(false, {
      nonNullable: true
    }),
    transactionInformationSubEscrow: new FormControl<boolean>(false, {
      nonNullable: true
    }),
    transactionInformationSurveyAmendmentSplit: new FormControl<boolean>(false, {
      nonNullable: true
    }),
    transactionInformationWarrantyPremiumAmount: new FormControl<string | null>(null),
    transactionInformationWaterBill: new FormControl<string | null>(null)
  });

  transactionInformationPropertyStates$: Observable<NetSheetNameValuePair[]>;
  transactionInformationPropertyCounties$: Observable<NetSheetNameValuePair[]>;
  transactionInformationPropertyCities$: Observable<NetSheetNameValuePair[]>;
  transactionInformationPropertyZips$: Observable<NetSheetNameValuePair[]>;

  transactionInformationEscrowStates$: Observable<NetSheetNameValuePair[]>;
  transactionInformationEscrowCounties$: Observable<NetSheetNameValuePair[]>;
  transactionInformationEscrowCities$: Observable<NetSheetNameValuePair[]>;
  transactionInformationEscrowBranches$: Observable<NetSheetNameValuePair[]>;

  fieldsSettings$ = new BehaviorSubject<{ [key: string]: NetSheetFormFieldSettings } | null>(null);

  constructor() {
    super();

    this.buyerCreditAmountLabel$ = this.store.select(
      fromNetSheetSelectors.selectTransactionInformationMiscBuyerCreditAmountLabel
    );

    this.form.controls.transactionInformationEscrowState.valueChanges.subscribe((transactionInformationEscrowState) => {
      this.store.dispatch(
        fromNetSheetActions.updateTransactionInformationLocation({
          payload: {
            transactionInformationEscrowState
          }
        })
      );
    });

    this.form.controls.transactionInformationEscrowCounty.valueChanges.subscribe(
      (transactionInformationEscrowCounty) => {
        this.store.dispatch(
          fromNetSheetActions.updateTransactionInformationLocation({
            payload: {
              transactionInformationEscrowCounty
            }
          })
        );
      }
    );

    this.form.controls.transactionInformationEscrowBranch.valueChanges.subscribe(
      (transactionInformationEscrowBranch) => {
        this.store.dispatch(
          fromNetSheetActions.updateTransactionInformationLocation({
            payload: {
              transactionInformationEscrowBranch
            }
          })
        );
      }
    );

    this.form.controls.transactionInformationEscrowCity.valueChanges.subscribe((transactionInformationEscrowCity) => {
      this.store.dispatch(
        fromNetSheetActions.updateTransactionInformationLocation({
          payload: {
            transactionInformationEscrowCity
          }
        })
      );
    });

    this.form.controls.transactionInformationPropertyState.valueChanges.subscribe(
      (transactionInformationPropertyState) => {
        this.store.dispatch(
          fromNetSheetActions.updateTransactionInformationLocation({
            payload: {
              transactionInformationPropertyState
            }
          })
        );
      }
    );

    this.form.controls.transactionInformationPropertyCounty.valueChanges.subscribe(
      (transactionInformationPropertyCounty) => {
        this.store.dispatch(
          fromNetSheetActions.updateTransactionInformationLocation({
            payload: {
              transactionInformationPropertyCounty
            }
          })
        );
      }
    );

    this.form.controls.transactionInformationPropertyCity.valueChanges.subscribe(
      (transactionInformationPropertyCity) => {
        this.store.dispatch(
          fromNetSheetActions.updateTransactionInformationLocation({
            payload: {
              transactionInformationPropertyCity
            }
          })
        );
      }
    );

    this.form.controls.transactionInformationPropertyZip.valueChanges.subscribe((transactionInformationPropertyZip) => {
      this.store.dispatch(
        fromNetSheetActions.updateTransactionInformationLocation({
          payload: {
            transactionInformationPropertyZip
          }
        })
      );
    });

    this.form.valueChanges.subscribe(() => {
      this.valid.set(this.form.valid);
    });

    this.form.valueChanges
      .pipe(
        map((data) =>
          omit(data, [
            'transactionInformationEscrowBranch',
            'transactionInformationEscrowCity',
            'transactionInformationEscrowCounty',
            'transactionInformationEscrowState',
            'transactionInformationPropertyCity',
            'transactionInformationPropertyCounty',
            'transactionInformationPropertyState',
            'transactionInformationPropertyZip'
          ])
        ),
        filter((data) => !isEmpty(data))
      )
      .subscribe((data) => {
        this.store.dispatch(
          fromNetSheetActions.updateTransactionInformation({
            payload: {
              ...data
            }
          })
        );
      });

    this.transactionInformationPropertyStates$ = this.store.pipe(
      select(fromNetSheetSelectors.selectTransactionInformationLocationPropertyStates),
      throttleTime(200, undefined, { leading: true, trailing: true }),
      distinctUntilChanged((previous, current) => isEqual(previous, current))
    );

    this.transactionInformationPropertyCounties$ = this.store.pipe(
      select(fromNetSheetSelectors.selectTransactionInformationLocationPropertyCounties),
      throttleTime(200, undefined, { leading: true, trailing: true }),
      distinctUntilChanged((previous, current) => isEqual(previous, current)),
      tap((items) =>
        items.length > 0
          ? this.form.controls.transactionInformationPropertyCounty.enable({ emitEvent: false })
          : this.form.controls.transactionInformationPropertyCounty.disable({ emitEvent: false })
      )
    );

    this.transactionInformationPropertyCities$ = this.store.pipe(
      select(fromNetSheetSelectors.selectTransactionInformationLocationPropertyCities),
      throttleTime(200, undefined, { leading: true, trailing: true }),
      distinctUntilChanged((previous, current) => isEqual(previous, current)),
      tap((items) =>
        items.length > 0
          ? this.form.controls.transactionInformationPropertyCity.enable({ emitEvent: false })
          : this.form.controls.transactionInformationPropertyCity.disable({ emitEvent: false })
      )
    );

    this.transactionInformationPropertyZips$ = this.store.pipe(
      select(fromNetSheetSelectors.selectTransactionInformationLocationPropertyZips),
      throttleTime(200, undefined, { leading: true, trailing: true }),
      distinctUntilChanged((previous, current) => isEqual(previous, current)),
      tap((items) =>
        items.length > 0
          ? this.form.controls.transactionInformationPropertyZip.enable({ emitEvent: false })
          : this.form.controls.transactionInformationPropertyZip.disable({ emitEvent: false })
      )
    );

    this.transactionInformationEscrowStates$ = this.store.pipe(
      select(fromNetSheetSelectors.selectTransactionInformationLocationEscrowStates),
      throttleTime(200, undefined, { leading: true, trailing: true }),
      distinctUntilChanged((previous, current) => isEqual(previous, current)),
      tap((items) =>
        items.length > 0
          ? this.form.controls.transactionInformationEscrowState.enable({ emitEvent: false })
          : this.form.controls.transactionInformationEscrowState.disable({ emitEvent: false })
      )
    );

    this.transactionInformationEscrowCounties$ = this.store.pipe(
      select(fromNetSheetSelectors.selectTransactionInformationLocationEscrowCounties),
      throttleTime(200, undefined, { leading: true, trailing: true }),
      distinctUntilChanged((previous, current) => isEqual(previous, current)),
      tap((items) =>
        items.length > 0
          ? this.form.controls.transactionInformationEscrowCounty.enable({ emitEvent: false })
          : this.form.controls.transactionInformationEscrowCounty.disable({ emitEvent: false })
      )
    );

    this.transactionInformationEscrowCities$ = this.store.pipe(
      select(fromNetSheetSelectors.selectTransactionInformationLocationEscrowCities),
      throttleTime(200, undefined, { leading: true, trailing: true }),
      distinctUntilChanged((previous, current) => isEqual(previous, current)),
      tap((items) =>
        items.length > 0
          ? this.form.controls.transactionInformationEscrowCity.enable({ emitEvent: false })
          : this.form.controls.transactionInformationEscrowCity.disable({ emitEvent: false })
      )
    );

    this.transactionInformationEscrowBranches$ = this.store.pipe(
      select(fromNetSheetSelectors.selectTransactionInformationLocationEscrowBranches),
      throttleTime(200, undefined, { leading: true, trailing: true }),
      distinctUntilChanged((previous, current) => isEqual(previous, current)),
      tap((items) =>
        items.length > 0
          ? this.form.controls.transactionInformationEscrowBranch.enable({ emitEvent: false })
          : this.form.controls.transactionInformationEscrowBranch.disable({ emitEvent: false })
      )
    );
  }

  ngOnInit() {
    this.addUniqueSubscription(
      'fieldsSettingsSubscription',
      this.store
        .pipe(
          select(fromNetSheetSelectors.selectTransactionInformationLocationFieldsSettings),
          throttleTime(200, undefined, { leading: true, trailing: true }),
          distinctUntilChanged((previous, current) => isEqual(previous, current))
        )
        .subscribe((fields) => {
          this.fieldsSettings$.next(fields);
          setTimeout(() => {
            this.valid.set(this.form.valid);
          });
        })
    );

    this.addUniqueSubscription(
      'fieldsDataSubscription',
      this.store
        .pipe(
          select(fromNetSheetSelectors.selectTransactionInformationLocationFieldsData),
          throttleTime(200, undefined, { leading: true, trailing: true }),
          distinctUntilChanged((previous, current) => isEqual(previous, current))
        )
        .subscribe((data) => {
          this.form.patchValue(data, { emitEvent: false });
          this.valid.set(this.form.valid);
        })
    );
  }

  async onDatePickerInputClick(event: Event) {
    event.stopImmediatePropagation();
    event.stopPropagation();
    event.preventDefault();

    if (!this.isDatePickerOpened) {
      this.isDatePickerOpened = true;
      const date = await this.datePickerService.open(
        this.form.controls.transactionInformationClosingDate.value ?? null,
        {
          min: DateTime.now().toFormat("yyyy-MM-dd'T'00:00:00")
        }
      );

      this.isDatePickerOpened = false;

      if (!isNull(date)) {
        this.form.patchValue({
          transactionInformationClosingDate: date
        });
      }
    }
  }

  readonly rateMask: MaskitoOptions = rateMask;
  readonly amountMask: MaskitoOptions = amountMask;
  readonly rateAmountMask: MaskitoOptions = rateAmountMask();
  readonly maskPredicate: MaskitoElementPredicate = async (el) =>
    (el as unknown as HTMLIonInputElement).getInputElement();
  readonly defaultSplitOptions = NetSheetSplitOptionsEnum;

  readonly valid = signal(this.form.valid);
}
